import React from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { RoutePaths } from 'constants/RoutePaths';
import { AppBar, Toolbar, Button, Stack, useTheme, Tabs, Tab, Box } from '@mui/material';
import LanguageDropdown from 'components/LanguageDropdown/LanguageDropdown';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'hooks/useAuthContext';
import WorkspaceDropdown from 'components/WorkspaceDropdown/WorkspaceDropdown';
import { InviteUserDialog } from 'components/views/inviteUser/InviteUserDialog/InviteUserDialog';
import { UserNameContextMenu } from 'components/UserNameContextMenu';
import { EmptyUserAvatar } from 'components/EmptyUserAvatar/EmptyUserAvatar';
import RoleGuard from 'context/auth/RoleGuard';
import { UserRoles, patronRoles } from 'constants/userRoles';
import { StyledAppBar, StyledToolbar, StyledNavbarStack, StyledTabs, StyledTab, StyledAddLessonButton, StyledLogo, StyledUserAvatar } from './NavbarComponent.styles';

// Wrapper component to handle routing without sx prop
const RoutedTab = ({ label, to }: { label: string; to: string }) => {
  const navigate = useNavigate();
  return (
    <StyledTab 
      label={label} 
      onClick={() => navigate(to)}
      data-testid={`navbar-tab-${label.toLowerCase()}`}
    />
  );
};

export const NavbarComponent = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const location = useLocation();
    const { userState } = useAuthContext();
    const navigate = useNavigate();

    const getCurrentTab = () => {
      switch (location.pathname) {
        case RoutePaths.HOME:
          return 0;
        case RoutePaths.PROFILE:
          return 1;
        case RoutePaths.TRAININGS:
          return 2;
        case RoutePaths.USERS_MANAGEMENT:
          return 3;
        case RoutePaths.LIVE:
          return 4;
        case RoutePaths.STATISTICS:
          return 5;
        default:
          return false;
      }
    };

    if (!userState.user.userId) {
        return null;
    }

    const userHasRequiredRole = (requiredRoles: string[]) => requiredRoles.some(role => role === userState.user.selectedWorkspace.userRole);

    return (
      <StyledAppBar position="relative">
          <StyledToolbar>
          <StyledNavbarStack
            direction={'row'}
            spacing={theme.spacing(3)}
            width='100%'
            height='100%'
            display={'flex'}
            justifyContent={'space-between'}
            alignContent={'center'}
            alignItems={'center'}
          >
            <Stack flexDirection={'row'} display='flex' justifyContent={'center'} alignItems={'center'}>
              <StyledLogo src={'logo.png'} alt='logo' $marginRight={theme.spacing(4)}/>
              <WorkspaceDropdown />
              <StyledTabs
                value={getCurrentTab()}
                indicatorColor="primary"
                textColor="primary"
                aria-label="Navbar Tabs"  
              >
                <RoutedTab label={t('navbar.home')} to={RoutePaths.HOME} />
                <RoutedTab label={t('navbar.profile')} to={RoutePaths.PROFILE} />
                {userHasRequiredRole(patronRoles) && (
                  <RoutedTab label={t('navbar.trainings')} to={RoutePaths.TRAININGS} />
                )}

                {userHasRequiredRole(patronRoles) && (
                  <RoutedTab label={t('navbar.users')} to={RoutePaths.USERS_MANAGEMENT} />
                )}

                {userHasRequiredRole([UserRoles.ADMIN, UserRoles.Patron]) && (
                  <RoutedTab label={t('navbar.live')} to={RoutePaths.LIVE} />
                )}

                {userHasRequiredRole(patronRoles) && (
                  <RoutedTab label={t('navbar.statistics')} to={RoutePaths.STATISTICS} />
                )}
              </StyledTabs>
            </Stack>            
            <Stack direction={'row'} spacing={theme.spacing(3)} alignItems={'center'} display='flex' justifyContent={'flex-end'} width={'60%'}>
              <RoleGuard requiredRoles={patronRoles}>
                <InviteUserDialog />
              </RoleGuard>
              <RoleGuard requiredRoles={[UserRoles.ADMIN, UserRoles.Patron]}>
              <StyledAddLessonButton 
                variant="outlined" 
                color='secondary' 
                onClick={() => navigate(RoutePaths.EDITOR)} 
              >
                {t('lessonEditor.addLessonButton')}
              </StyledAddLessonButton>
              </RoleGuard>
              <UserNameContextMenu />
              {userState.user.selectedWorkspace.userPhotoUrl ? 
                <StyledUserAvatar src={userState.user.selectedWorkspace.userPhotoUrl} alt="User Avatar" />
                : <EmptyUserAvatar color='black' fontSize='25px' padding={theme.spacing(1)} />
              }
              <LanguageDropdown />
            </Stack>
          </StyledNavbarStack>
        </StyledToolbar>
      </StyledAppBar>
    );
  };